import { Blackboard } from "./Blackboard";
import { CurrentState, OfflineUpdateSummary } from "./OfflineUpdateSummary";
import Auth from "../auth/Auth";
import { UpdateDeltaCalculator } from "./UpdateDeltaCalculator";
import { FetchWithAuth } from "../common/FetchWithAuth";
import {
  addToCache,
  deleteSongsheetFile,
  findAllSongsheetCacheKeys,
} from "./CacheManager";
import { SongUb } from "../gql/graphql";
import i18n from "../i18n/config";

// Magic constant. Browsers do - depending on HTTP level - allow something between 12 and 40 connections per domain.
const CONCURRENT_DOWNLOADS = 25;

export async function update(
  songs: readonly SongUb[],
  auth: Auth,
  notifyProgress: (
    value:
      | ((prevState: OfflineUpdateSummary) => OfflineUpdateSummary)
      | OfflineUpdateSummary,
  ) => void,
): Promise<void> {
  try {
    const blackboard = new Blackboard(notifyProgress, songs);

    await findFilenamesInCache(blackboard);
    await calculateDelta(blackboard);
    await deleteFiles(blackboard);
    await downloadFiles(blackboard, auth);

    const finishNotification =
      blackboard.failures.length > 0
        ? CurrentState.FINISHED_WITH_ERRORS
        : CurrentState.FINISHED;
    blackboard.updateAndNotifyCurrentState(finishNotification);
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    console.error(message);
    notifyProgress({ currentState: CurrentState.ABORTED_WITH_ERROR });
  }
}

async function findFilenamesInCache(
  blackboard: Blackboard,
): Promise<Blackboard> {
  blackboard.updateAndNotifyCurrentState(
    CurrentState.CHECKING_CURRENT_DATA_IN_CACHE,
  );
  const cacheUrls = await findAllSongsheetCacheKeys();
  blackboard.setUrlsFromCache(new Set(cacheUrls));
  return blackboard;
}

async function calculateDelta(blackboard: Blackboard): Promise<Blackboard> {
  blackboard.updateAndNotifyCurrentState(CurrentState.CALCULATE_DELTA);
  blackboard.work = new UpdateDeltaCalculator().calculateDelta(
    blackboard.input.urlsFromToc,
    blackboard.input.urlsFromCache,
  );
  return blackboard;
}

async function downloadFiles(
  blackboard: Blackboard,
  auth: Auth,
): Promise<Blackboard> {
  const currentLengthBeforeBatch = blackboard.work.urlsToBeDownloaded.length;

  if (currentLengthBeforeBatch > 0) {
    blackboard.updateAndNotifyCurrentState(CurrentState.DOWNLOAD);
    blackboard.updateDownloadsLeft(currentLengthBeforeBatch);
    const urls = blackboard.work.urlsToBeDownloaded.splice(
      0,
      CONCURRENT_DOWNLOADS,
    );
    let finished = 0;
    const fetchPromises = urls.map(async (url) => {
      try {
        await addUrlToCache(url, auth);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        blackboard.failures.push(
          "Fehler beim Download von " + url + ": " + message,
        );
      } finally {
        ++finished;
        blackboard.updateDownloadsLeft(currentLengthBeforeBatch - finished);
      }
    });
    await Promise.allSettled(fetchPromises);
    return downloadFiles(blackboard, auth);
  } else {
    return blackboard;
  }
}

async function addUrlToCache(url: string, auth: Auth) {
  const response = await new FetchWithAuth(auth).fetch(url);
  return addToCache(url, response);
}

async function deleteFiles(blackboard: Blackboard) {
  blackboard.updateAndNotifyCurrentState(CurrentState.DELETE_OLD);
  const promises = [...blackboard.work.urlsToBeDeleted].map((filename) =>
    deleteSongsheetFile(filename),
  );
  try {
    await Promise.all(promises);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    blackboard.failures.push(
      `${i18n.t("offline.deleteOldDataError")}: ${message}`,
    );
  }
  return blackboard;
}
